/**
 * cmd -> /api route table.
 *
 * The composer emits ~95 legacy `cmd` strings from 150+ call sites, and some of
 * them are built DYNAMICALLY (LocaleViewerPage picks one of four epack-list
 * cmds at runtime; AIAssessmentPage picks one of two progress cmds;
 * AdminTaskDialog forwards an arbitrary cmd through apiRequestPromise). A
 * per-call-site rewrite would miss those, so the mapping lives here as a total
 * table and API.jsx stays a thin adapter with its original signatures.
 *
 * A resolver returns a descriptor:
 *   { method, path, query, body, envelope, onError, special, warn }
 * where
 *   - `path`     is api-relative ("api/epack/12"),
 *   - `query`    becomes the query string (arrays become repeated params),
 *   - `body`     is JSON-encoded (omitted entirely when undefined),
 *   - `envelope` optionally rewrites the {result,count,cmd,remarks} body,
 *   - `onError`  optionally maps a DESIGNED non-2xx (payload, status) back to a
 *                legacy 200-shaped body; return null to fall through to the
 *                normal {result:-1, remarks} failure,
 *   - `special`  asks API.jsx for behaviour no single HTTP call can provide
 *                ('noop', 'logout', 'unsupported', 'batch_progress'),
 *   - `warn`     is logged once: the route exists but cannot express the
 *                filter the caller asked for (see GAP-1).
 *
 * An UNKNOWN cmd resolves to null and API.jsx fails loudly.
 */

// --------------------------------------------------------------- utilities

/** Query values: drop undefined/null/"" (an empty filter is "not sent"). */
function q(obj) {
  const out = {};
  Object.keys(obj || {}).forEach((k) => {
    const v = obj[k];
    if (v === undefined || v === null || v === "") {
      return;
    }
    out[k] = (v === true) ? 1 : (v === false ? 0 : v);
  });
  return out;
}

/** Body values: drop undefined/null, keep "" and 0, booleans become 1/0. */
function b(obj) {
  const out = {};
  Object.keys(obj || {}).forEach((k) => {
    const v = obj[k];
    if (v === undefined || v === null) {
      return;
    }
    out[k] = (v === true) ? 1 : (v === false ? 0 : v);
  });
  return out;
}

/**
 * Pick `keys` off a row for a request model that declares extra="forbid" —
 * sending anything else is a 422, not a silently ignored field. `numeric` keys
 * are dropped when they are blank/non-numeric ("" is what the composer sends
 * for "no cover picked", and the model types those columns as ints).
 */
function pick(row, keys, numeric = []) {
  const src = row || {};
  const numericSet = new Set(numeric);
  const out = {};
  keys.forEach((k) => {
    if (!(k in src)) {
      return;
    }
    let v = src[k];
    if (v === undefined || v === null) {
      return;
    }
    if (v === true) { v = 1; }
    if (v === false) { v = 0; }
    if (numericSet.has(k)) {
      if (v === "" || (typeof v === 'string' && isNaN(Number(v)))) {
        return;
      }
    }
    out[k] = v;
  });
  return out;
}

const f = (r, key) => ((r && r.filter) ? r.filter[key] : undefined);

/**
 * Create routes answer {"data": {"id": N}}, but every caller of the legacy
 * `insert` reads the new id straight off res.body.result (addHotspotOK,
 * createLocaleOK, subscriptionCreateOK, ContentDetailView's insertId||result).
 */
const idEnvelope = (data) => ({
  result: (data && typeof data === 'object') ? data.id : data,
  count: 1,
  insertId: (data && typeof data === 'object') ? data.id : undefined,
});

/**
 * Write routes answer {"data": {"ok": true, ...}}; the legacy surface answered
 * a bare 1 and these callers test `res.body.result == 1` before reporting
 * success (AdminTaskDialog's editUserOK / deleteUserOK / passwordChangeOK /
 * editSchoolOK).
 */
const okEnvelope = () => ({ result: 1, count: 1 });

// The write models' allow-lists, mirrored from ev_web_v2/app/surfaces/api.
//
// This mirror is HAND-MAINTAINED and nothing verifies it. Adding a key the
// server's model does not declare is a 422 on EVERY request through that route,
// not a silently ignored field — that is exactly what broke locale saving in
// production between 2026-08-07 and 2026-08-09. Add a key here only after the
// server model accepts it AND that server build is deployed; the API ships
// first, the composer second.
//
// The five avatar-interaction flags below are accepted by the API but NOT yet
// persisted — their DB columns do not exist. They will appear to save and then
// revert on reload until the migration lands. See PENDING_DB_MIGRATIONS.md in
// the ev_api_v2 repo.
const EPACK_KEYS = [
  "name", "description", "kla", "subject", "color", "lang", "is_share", "is_ready",
  "is_duplication_allowed", "mid_lat", "mid_lon", "ul_lat", "ul_lon", "br_lat", "br_lon",
  "tn_file_id", "hs_reveal_code", "hs_reveal_code_enabled", "is_auto_share_assessment",
  "is_manual_ai_assessment", "is_mood_enabled", "is_nudge_enabled",
  "is_chat_teacher_to_student", "is_chat_student_to_student", "is_chat_student_to_teacher",
];
const EPACK_NUMERIC = [
  "kla", "subject", "color", "is_share", "is_ready", "is_duplication_allowed",
  "mid_lat", "mid_lon", "ul_lat", "ul_lon", "br_lat", "br_lon", "tn_file_id",
  "hs_reveal_code_enabled", "is_auto_share_assessment", "is_manual_ai_assessment",
  "is_mood_enabled", "is_nudge_enabled", "is_chat_teacher_to_student",
  "is_chat_student_to_student", "is_chat_student_to_teacher",
];
const COMPONENT_CREATE_KEYS = [
  // epack_id is deliberately absent: ComponentCreateIn forbids it and derives it
  // from hotspot_id server-side.
  "hotspot_id", "name", "kind", "display_index", "diff", "content", "payload", "score_max",
];
const COMPONENT_UPDATE_KEYS = ["name", "diff", "content", "payload", "score_max"];
const HOTSPOT_KEYS = ["name", "color", "radius", "lat", "lon", "disclose"];

// -------------------------------------------------------- generic CRUD table

/**
 * The legacy generic cmds resolve by `reqObj.table`; each (cmd, table) pair is
 * its own route on the new surface (the free-form `table`/`filter` shape is
 * exactly what the SQLi hardening removed).
 */
const GENERIC = {
  get_list: {
    kla: () => ({ method: 'GET', path: 'api/taxonomy/klas' }),
    subject: () => ({ method: 'GET', path: 'api/taxonomy/subjects' }),
    component: (r) => {
      // Two distinct readers share cmd+table: by hotspot (the content list) and
      // by id (the 3D-model refresh probe).
      if (f(r, 'hotspot_id') !== undefined) {
        return { method: 'GET', path: 'api/component/byHotspot', query: q({ hotspot_id: f(r, 'hotspot_id') }) };
      }
      return {
        method: 'GET',
        path: 'api/component/' + encodeURIComponent(f(r, 'id')),
        // The route returns ONE object; the caller does list[0] on an array.
        envelope: (data, count) => ({ result: (data ? [data] : []), count: (data ? 1 : 0) }),
      };
    },
    // FLAT hotspot rows. /api/hotspot/byEpack is the NESTED shape and is used by
    // get_hotspot_by_epack_id instead.
    hotspot: (r) => ({ method: 'GET', path: 'api/epack/hotspots', query: q({ epack_id: f(r, 'epack_id') }) }),
  },
  insert: {
    epack: (r) => ({
      method: 'POST',
      path: 'api/epack',
      // school_id is honoured for admins only and user_id does not exist on the
      // model — both are stamped from the token (that was the IDOR).
      body: Object.assign(pick(r.row, EPACK_KEYS, EPACK_NUMERIC), pick(r.row, ["school_id"], ["school_id"])),
      envelope: idEnvelope,
    }),
    hotspot: (r) => ({
      method: 'POST',
      path: 'api/hotspot',
      body: Object.assign({ epack_id: r.row.epack_id }, pick(r.row, HOTSPOT_KEYS, ["color", "radius", "lat", "lon", "disclose"])),
      envelope: idEnvelope,
    }),
    component: (r) => ({
      method: 'POST',
      path: 'api/component',
      body: pick(r.row, COMPONENT_CREATE_KEYS, ["hotspot_id", "kind", "display_index", "diff", "score_max"]),
      envelope: idEnvelope,
    }),
  },
  update: {
    // Identity comes from the token; the client's own user id is dropped.
    user: (r) => ({ method: 'PATCH', path: 'api/user/me', body: pick(r.row, ["display_name", "email"]) }),
    epack: (r) => ({ method: 'PATCH', path: 'api/epack/' + encodeURIComponent(r.where_value), body: pick(r.row, EPACK_KEYS, EPACK_NUMERIC) }),
    file: (r) => ({ method: 'PATCH', path: 'api/file/' + encodeURIComponent(r.where_value), body: pick(r.row, ["epack_id"], ["epack_id"]) }),
    component: (r) => ({ method: 'PATCH', path: 'api/component/' + encodeURIComponent(r.where_value), body: pick(r.row, COMPONENT_UPDATE_KEYS, ["diff", "score_max"]) }),
    hotspot: (r) => ({ method: 'PATCH', path: 'api/hotspot/' + encodeURIComponent(r.where_value), body: pick(r.row, HOTSPOT_KEYS, ["color", "radius", "lat", "lon", "disclose"]) }),
  },
  delete: {
    component: (r) => ({ method: 'DELETE', path: 'api/component/' + encodeURIComponent(f(r, 'id')) }),
    hotspot: (r) => ({ method: 'DELETE', path: 'api/hotspot/' + encodeURIComponent(f(r, 'id')) }),
    epack: (r) => ({ method: 'DELETE', path: 'api/epack/' + encodeURIComponent(f(r, 'id')) }),
  },
};

// ------------------------------------------------------------- the cmd table

const ROUTES = {

  // ---------------------------------------------------------------- session
  do_logout: () => ({ special: 'logout' }),

  // ------------------------------------------------------------------ epack
  // The library's default tab. is_editor / show_public_only / qa_state and the
  // map bbox are now DECLARED on GET /api/epack/search (the service always
  // implemented them; the route just never named them), so unpublished drafts
  // and the viewport filter work again.
  //
  // per_page is defaulted rather than left to the route: LocaleViewerPage only
  // sets current_page/row_per_page in LIST view, and the route's own default is
  // 20 while the legacy service defaulted to 100 — the map silently plotted 20
  // of 100 locales.
  get_list_epack: (r) => ({
    method: 'GET',
    path: 'api/epack/search',
    query: q({
      keyword: f(r, 'epack.name'),
      kla: f(r, 'epack.kla'),
      subject: f(r, 'epack.subject'),
      school_id: f(r, 'epack.school_id'),
      epack_id: f(r, 'epack.id'),
      order_by: r.order_by,
      order_mode: r.order_mode,
      page: r.current_page,
      per_page: r.row_per_page || 100,
      is_editor: r.is_editor,
      show_public_only: r.show_public_only,
      qa_state: f(r, 'qa_state'),
      ul_lat: f(r, 'ul_lat'),
      ul_lon: f(r, 'ul_lon'),
      br_lat: f(r, 'br_lat'),
      br_lon: f(r, 'br_lon'),
    }),
  }),
  // The three library tabs are three DISTINCT queries, and mapping them all to
  // GET /api/epack/list was wrong in three ways: no user filter (an admin's "My
  // Locale" listed every epack in the database), no "has my school's
  // submissions" filter, and no cover join (blank thumbnails). Each now has its
  // own route backed by the same service the legacy surface calls.
  get_list_epack_by_user_id: (r) => ({
    method: 'GET',
    path: 'api/epack/byUser',
    query: q({ user_id: r.user_id, order_by: r.order_by, order_mode: r.order_mode, keyword: r.keyword }),
  }),
  get_list_epack_by_school_id: (r) => ({
    method: 'GET',
    path: 'api/epack/bySchool',
    query: q({ school_id: r.school_id, order_by: r.order_by, order_mode: r.order_mode, keyword: r.keyword }),
  }),
  get_list_epack_has_my_sch_submission: (r) => ({
    method: 'GET',
    path: 'api/epack/withSchoolSubmissions',
    query: q({ school_id: r.school_id, order_by: r.order_by, order_mode: r.order_mode, keyword: r.keyword }),
  }),
  // The fourth library tab: Extra Locale (預製教材範本) templates this school
  // selected. NO school_id — the route reads it from the token and resolves the
  // epack ids from the selection ledger, so it cannot be aimed at another
  // school. Sending one anyway would be a 422 (the route declares no such param).
  get_list_epack_extra_locale: (r) => ({
    method: 'GET',
    path: 'api/epack/extraLocaleSelected',
    query: q({ order_by: r.order_by, order_mode: r.order_mode, keyword: r.keyword }),
  }),

  // Freshness bookkeeping only. content_write.py folds date_update in after
  // every content write, so this is a genuine server-side no-op — it must
  // resolve to a synthetic success, never to an unknown-cmd error (10 sites).
  touch_epack: () => ({ special: 'noop' }),

  get_epack_list_allows_duplicate: () => ({ method: 'GET', path: 'api/epack/duplicatable' }),
  duplicate_epack: (r) => ({
    method: 'POST',
    path: 'api/epack/duplicate',
    body: b({ epack_id: r.epack_id, new_name: r.new_name, school_id: r.school_id }),
    // The route signals failure with a non-2xx status and returns no `success`
    // flag; ResourceLibraryPage.duplicateSuccess still gates on result.success.
    envelope: (data, count) => ({ result: Object.assign({ success: true }, data), count: count }),
  }),
  is_epack_duplicate: (r) => ({ method: 'GET', path: 'api/epack/isDuplicate', query: q({ epack_id: r.epack_id }) }),
  get_epacks_by_ids: (r) => ({
    method: 'GET',
    path: 'api/epack/byIds',
    // The caller sends an ARRAY; the route takes a comma-separated string.
    query: q({ ids: Array.isArray(r.ids) ? r.ids.join(',') : r.ids }),
  }),
  search_epack_by_keyword: (r) => ({
    method: 'GET',
    path: 'api/epack/searchByKeyword',
    query: q({
      keyword: r.keyword,
      search_mode: r.search_mode,
      exclude_subscription_id: r.exclude_subscription_id,
      source_id: r.source_id,
      limit: r.limit,
      // Admin-only flags (the server forces them off for non-admins): the
      // Extra Locale template picker needs them because the 26/27 catalogue
      // epacks are drafts (is_ready = 0) that a default search would hide.
      include_unready: r.include_unready,
      include_hidden: r.include_hidden,
    }),
  }),
  search_epack_sources: (r) => ({
    method: 'GET',
    path: 'api/epack/searchSources',
    query: q({
      keyword: r.keyword,
      search_mode: r.search_mode,
      exclude_subscription_id: r.exclude_subscription_id,
      limit: r.limit,
    }),
  }),
  get_file_path_by_epack_id: (r) => ({ method: 'GET', path: 'api/epack/files', query: q({ epack_id: r.epack_id }) }),
  // NESTED {hotspot_list: ...} shape — LocaleOverviewDialog reads result.hotspot_list.
  get_hotspot_by_epack_id: (r) => ({
    method: 'GET', path: 'api/hotspot/byEpack', query: q({ epack_id: r.epack_id }),
    // /api/hotspot/byEpack returns a BARE ARRAY of {hotspot_detail} items (the
    // shape the Flutter app reads). The legacy cmd wrapped it as
    // {hotspot_list: [...]}, and LocaleOverviewDialog.getHotspotListOK still reads
    // result.hotspot_list — so without this the overview's hotspot list was
    // always empty ("Click EDIT to start creating hotspot") even for locales that
    // have hotspots. Re-add the wrapper for the composer; the API is unchanged so
    // the app is unaffected. Inner items already carry hotspot_detail.
    envelope: (data, count) => ({
      result: { hotspot_list: Array.isArray(data) ? data : [] },
      count: Array.isArray(data) ? data.length : 0,
    }),
  }),
  update_qa_state_by_epack_id: (r) => ({
    method: 'PATCH',
    path: 'api/epack/' + encodeURIComponent(r.epack_id) + '/qaState',
    body: b({ qa_state: r.qa_state }),
    // The route names the column `epack_id`; LocaleOverviewDialog.updateQAstateOK
    // reads `locale_id` (and uses it to patch its local row).
    envelope: (data) => ({
      result: { locale_id: (data || {}).epack_id, qa_state: (data || {}).qa_state },
      count: 1,
    }),
  }),
  reset_epack_submission_new: (r) => ({ method: 'POST', path: 'api/epack/' + encodeURIComponent(r.epack_id) + '/resetSubmissionBadge' }),
  get_npc_limit_per_locale: (r) => ({ method: 'GET', path: 'api/epack/npcLimit', query: q({ epack_id: r.epack_id }) }),
  get_assessible_answers_count_by_epack_id: (r) => ({ method: 'GET', path: 'api/epack/' + encodeURIComponent(r.epack_id) + '/assessibleAnswersCount' }),
  get_epack_quota: (r) => ({ method: 'GET', path: 'api/subscription/epackQuota', query: q({ school_id: r.school_id }) }),
  delete_file: (r) => ({ method: 'DELETE', path: 'api/file/' + encodeURIComponent(r.file_id) }),

  // Both legacy ordering cmds collapse into one positional route. The call
  // sites in HotspotDetailView were updated to send {hotspot_id, component_ids}
  // because the server requires the submitted set to equal the hotspot's set.
  set_components_display_index: (r) => ({
    method: 'POST',
    path: 'api/component/reorder',
    body: b({ hotspot_id: r.hotspot_id, component_ids: r.component_ids }),
  }),
  swap_components_display_index: (r) => ({
    method: 'POST',
    path: 'api/component/reorder',
    body: b({ hotspot_id: r.hotspot_id, component_ids: r.component_ids }),
  }),
  // dry_run must stay a real boolean. b() would turn true into 1.
  import_locale_content: (r) => ({
    method: 'POST',
    path: 'api/component/import',
    body: {
      epack_id: r.epack_id,
      dry_run: !!r.dry_run,
      rows: Array.isArray(r.rows) ? r.rows : [],
    },
    onError: (payload, status) => {
      const detail = payload && payload.detail;
      if ((status === 422 || status === 409) && detail && typeof detail === 'object' && !Array.isArray(detail)) {
        return {
          result: -1,
          errors: Array.isArray(detail.errors) ? detail.errors : [],
          warnings: Array.isArray(detail.warnings) ? detail.warnings : [],
          error_count: detail.error_count || 0,
          warning_count: detail.warning_count || 0,
        };
      }
      return null;
    },
  }),

  // -------------------------------------------------------------- npc / misc
  get_npc_personality_list: () => ({ method: 'GET', path: 'api/npc/personality/list' }),
  get_npc_avatar_list: () => ({ method: 'GET', path: 'api/npc/avatar/list' }),
  get_npc_language_list: () => ({ method: 'GET', path: 'api/npc/language/list' }),
  get_curriculum_options: () => ({ method: 'GET', path: 'api/curriculum/options' }),
  get_available_azure_models: () => ({ method: 'GET', path: 'api/ai/models' }),

  // --------------------------------------------------------------------- AI
  get_AI_response: (r) => ({
    method: 'POST',
    path: 'api/ai/suggest',
    body: pick(r, [
      "locale_name", "hotspot_name", "content_types", "model", "output_language",
      "curriculum_kla", "curriculum_subject", "curriculum_level",
    ]),
  }),
  translate_locale: (r) => ({ method: 'POST', path: 'api/ai/translateLocale', body: pick(r, ["epack_id", "target_language", "model"]) }),
  get_ai_assessment_v2: (r) => ({ method: 'POST', path: 'api/ai/assess', body: pick(r, ["answer_id", "model"]) }),
  get_latest_ai_assessment: (r) => ({
    method: 'GET',
    path: 'api/ai/latestAssessment',
    query: q({ answer_id: r.answer_id }),
    // The route WRAPS the service result as {"assessment": ...}; the legacy cmd
    // returned it bare, and all three call sites read `result` /
    // `result.ai_processed_item.response` straight off res.body.result. Without
    // unwrapping, every assessment panel renders empty.
    envelope: (data, count) => ({
      result: (data && typeof data === 'object' && 'assessment' in data) ? data.assessment : data,
      count: count,
    }),
  }),
  get_all_ai_assessment_response: (r) => ({
    method: 'GET',
    path: 'api/ai/assessmentResponses',
    query: q({ epack_id: r.locale_id, date: r.date, school_id: r.school_id }),
  }),
  get_submission_summary: (r) => ({ method: 'POST', path: 'api/ai/submissionSummary', body: pick(r, ["submission_id", "model"]) }),
  get_transcription: (r) => ({ method: 'POST', path: 'api/ai/transcription', body: b({ answer_id: r.answer_id, force: r.force ? true : false }) }),
  check_transcription: (r) => ({ method: 'GET', path: 'api/ai/transcription/check', query: q({ answer_id: r.answer_id }) }),
  // `path` is dropped: the route resolves the blob from file_id itself.
  get_voice_over_transcription: (r) => ({ method: 'POST', path: 'api/ai/voiceOverTranscription', body: b({ file_id: r.file_id }) }),
  count_pending_manual_ai_assessment_by_epack_and_date: (r) => ({
    method: 'GET',
    path: 'api/ai/pendingManualCount',
    query: q({ epack_id: r.locale_id, date: r.date }),
  }),
  queue_manual_ai_assessment_by_epack_and_date: (r) => ({
    method: 'POST',
    path: 'api/ai/queueManualAssessment',
    body: b({ epack_id: r.locale_id, date: r.date }),
  }),
  get_ai_3d_quota: (r) => ({ method: 'GET', path: 'api/ai3d/quota', query: q({ school_id: r.school_id }) }),

  // Image → 3D self-host mode (ThreeDJobManager). The claim row keeps the cron
  // pipeline off self-handled components; flipping it to -1 is the Hunyuan
  // fallback. Routes exist only on an updated server — the manager treats a
  // failure as "server too old" and stays on the cloud path.
  do_insert_ai_3d_gen_item: (r) => ({
    method: 'POST',
    path: 'api/ai3d/genItem',
    body: b({
      component_id: r.component_id, status: r.status, img_url_1: r.img_url_1,
      tier: r.tier, EnablePBR: r.EnablePBR, FaceCount: r.FaceCount, GenerateType: r.GenerateType,
      response: r.response,
    }),
  }),
  update_ai_3d_gen_item: (r) => ({
    method: 'PATCH',
    path: 'api/ai3d/genItem/' + encodeURIComponent(r.component_id),
    body: b({
      status: r.status, img_url_1: r.img_url_1,
      tier: r.tier, EnablePBR: r.EnablePBR, FaceCount: r.FaceCount, GenerateType: r.GenerateType,
      // Self-job ground truth riders: metadata JSON, and the stored GLB's
      // file row on the 105 transition (the route redeems quota with it).
      response: r.response, file_id: r.file_id,
    }),
  }),
  redeem_3d_model_quota: (r) => ({
    method: 'POST',
    path: 'api/ai3d/redeem',
    body: b({ component_id: r.component_id, file_id: r.file_id }),
  }),
  // Server-mediated self-host generation: the SERVER holds the platform
  // credential, checks quota BEFORE submitting, records the job row, and
  // proxies status/cancel — the browser never contacts the GPU platform.
  generate_3d_self: (r) => ({
    method: 'POST',
    path: 'api/ai3d/generate',
    body: b({
      component_id: r.component_id, prompt: r.prompt, engine: r.engine, quality: r.quality,
      tier: r.tier, EnablePBR: r.EnablePBR, FaceCount: r.FaceCount, GenerateType: r.GenerateType,
    }),
  }),
  get_3d_self_status: (r) => ({
    method: 'GET',
    path: 'api/ai3d/genItem/' + encodeURIComponent(r.component_id) + '/status',
  }),
  cancel_3d_self: (r) => ({
    method: 'POST',
    path: 'api/ai3d/genItem/' + encodeURIComponent(r.component_id) + '/cancel',
  }),
  // Startup handshake (public). Answers ai3dGenMode/ai3dSelfApiUrl on an
  // updated server; older servers just omit the keys.
  get_system_config: () => ({ method: 'GET', path: 'api/system/config' }),

  // GAP-2: the fine-grained batch-progress cmds are legacy-only. Silently
  // no-oping them would spin the 2s poller forever, so API.jsx synthesises the
  // progress object from the answer counters instead (see 'batch_progress').
  get_ai_assessment_batch_progress: () => ({ special: 'batch_progress' }),
  get_resume_ai_assessment_batch_progress: () => ({ special: 'batch_progress' }),

  // ------------------------------------------------------ submission/answer
  get_submission_groups_by_epack: (r) => ({
    method: 'GET',
    path: 'api/submission/groupsByEpack',
    query: q({ locale_id: r.locale_id, school_id: r.school_id, page_index: r.page_index, item_per_page: r.item_per_page }),
  }),
  get_submission_list_by_epack_and_date: (r) => ({
    method: 'GET',
    path: 'api/submission/listByEpackAndDate',
    query: q({ locale_id: r.locale_id, date: r.date, school_id: r.school_id }),
  }),
  // Public, token-scoped answer detail for the share page. Mapped like any
  // other cmd so SubmissionDetail keeps using API.request().
  get_shared_answers_by_token: (r) => ({
    method: 'GET',
    path: 'api/submission/byToken/answers',
    query: q({ token: r.token }),
  }),
  get_submission_by_token: (r) => ({ method: 'GET', path: 'api/submission/byToken', query: q({ token: r.token }) }),
  get_submission_score: (r) => ({ method: 'GET', path: 'api/submission/score', query: q({ submission_id: r.submission_id }) }),
  is_submission_sharable: (r) => ({ method: 'GET', path: 'api/submission/' + encodeURIComponent(r.submission_id) + '/sharable' }),
  // school_id is deliberately NOT sent: on the legacy cmd it DECIDED permission,
  // so a body-supplied value made any submission publicly shareable.
  share_submission: (r) => ({
    method: 'POST',
    path: 'api/submission/' + encodeURIComponent(r.submission_id) + '/share',
    // Route answers {ok, token}; RetrieverPage gates on result.success.
    envelope: (data) => ({ result: { success: true, token: (data || {}).token || "" }, count: 1 }),
  }),

  get_answers_by_submission_id: (r) => ({ method: 'GET', path: 'api/answer/bySubmission', query: q({ submission_id: r.submission_id }) }),
  get_all_answers_in_submission_list: (r) => ({
    method: 'GET',
    path: 'api/answer/bySubmissionList',
    query: q({ ids: r.submission_id_list }),
  }),
  // request_ans aliases — same reads, CSV/XLSX is built client-side.
  download_ans: (r) => ({ method: 'GET', path: 'api/answer/bySubmission', query: q({ submission_id: r.submission_id }) }),
  download_ans_batch: (r) => ({ method: 'GET', path: 'api/answer/bySubmissionList', query: q({ ids: r.submission_id_list }) }),

  get_answer_score: (r) => ({ method: 'GET', path: 'api/answer/' + encodeURIComponent(r.answer_id) + '/score' }),
  set_answer_score: (r) => ({ method: 'PATCH', path: 'api/answer/' + encodeURIComponent(r.answer_id) + '/score', body: b({ score: r.new_score }) }),
  edit_answer_score: (r) => ({
    method: 'PATCH',
    path: 'api/answer/' + encodeURIComponent(r.answer_id) + '/score',
    body: b({ score: r.score }),
    // SubmissionDetailContent reads res.body.success (not res.body.result.success)
    // on this one cmd; without it the OK path always takes the "failed" branch.
    envelope: (data, count) => ({ result: data, count: count, success: true }),
  }),
  get_total_answers_number_by_epack_and_date: (r) => ({
    method: 'GET',
    path: 'api/answer/totalCount',
    query: q({ epack_id: r.locale_id, date: r.date, school_id: r.school_id }),
  }),
  get_total_assessed_answers_number_by_epack_and_date: (r) => ({
    method: 'GET',
    path: 'api/answer/assessedCount',
    query: q({ epack_id: r.locale_id, date: r.date, school_id: r.school_id }),
  }),

  // ------------------------------------------------------- user / school / storage
  search_user: (r) => ({ method: 'GET', path: 'api/user/search', query: q({ keyword: r.search_term }) }),
  user_update_password: (r) => ({ method: 'POST', path: 'api/user/updatePassword', body: pick(r, ["old_password", "new_password"]) }),
  user_accept_agreement: (r) => ({ method: 'POST', path: 'api/user/acceptAgreement', body: b({ agreement_version: r.agreement_version }) }),
  user_reset_password: (r) => ({ method: 'POST', path: 'api/user/requestPasswordReset', body: pick(r, ["recaptcha_token", "email"]) }),
  // GAP-3: never ported, and there was no PHP handler either — the dialog that
  // sends it is unreachable dead code (LoginPage.forgetPasswordClicked has no
  // caller; the live flow is user_reset_password + reCAPTCHA).
  user_forget_password: () => ({ special: 'unsupported', reason: 'user_forget_password has no handler on either surface; use the reCAPTCHA reset flow.' }),
  get_edconnect_accounts: () => ({ method: 'GET', path: 'api/user/edconnectAccounts' }),
  unbind_edconnect: (r) => ({ method: 'DELETE', path: 'api/user/edconnectAccount/' + encodeURIComponent(r.account_id) }),

  get_school_list: () => ({ method: 'GET', path: 'api/school/list' }),
  get_filtered_school_list: (r) => ({
    method: 'GET',
    path: 'api/school/filtered',
    query: q({ keyword: r.keyword, school_type: r.school_type, school_level: r.school_level, order_by: r.order_by, order: r.order, limit: r.limit, admin_view: r.admin_view }),
  }),
  create_school: (r) => ({ method: 'POST', path: 'api/school', body: pick(r, ["name", "name_eng", "key", "locale", "school_type", "school_level"]) }),
  get_user_data_by_school_id: (r) => ({ method: 'GET', path: 'api/school/users', query: q({ school_id: r.school_id }) }),
  get_user_storage_summary: (r) => ({ method: 'GET', path: 'api/storage/userSummary', query: q({ user_id: r.user_id }) }),
  get_school_users_storage: (r) => ({ method: 'GET', path: 'api/storage/schoolUsers', query: q({ school_id: r.school_id }) }),
  get_privilege_detail_by_school_id: (r) => ({ method: 'GET', path: 'api/privilege/detailBySchool', query: q({ school_id: r.school_id }) }),
  get_maintenance_message: (r) => ({ method: 'GET', path: 'api/app/maintenanceMessage', query: q({ kind: r.kind, user_id: r.user_id }) }),
  maintenance_message_read: (r) => ({ method: 'POST', path: 'api/app/maintenanceMessageRead', body: b({ message_id: r.message_id, do_not_show: r.do_not_show }) }),

  // ------------------------------------------------------------------ admin
  user_admin_create_user: (r) => ({
    method: 'POST',
    path: 'api/admin/user',
    body: pick(r, ["username", "display_name", "email", "role", "school_id", "new_password"], ["role", "school_id"]),
  }),
  user_admin_batch_create_users: (r) => ({
    method: 'POST',
    path: 'api/admin/user/batchCreate',
    // admin_id is dropped — the admin is the token.
    body: b({ users: r.users, validate_only: r.validate_only, admin_password: r.admin_password }),
  }),
  admin_edit_user: (r) => ({
    method: 'PATCH',
    path: 'api/admin/user/' + encodeURIComponent(r.target_user_id),
    body: pick(r, ["admin_password", "display_name", "email", "role", "school_id"], ["role", "school_id"]),
    envelope: okEnvelope,
  }),
  admin_delete_user: (r) => ({
    method: 'DELETE',
    path: 'api/admin/user/' + encodeURIComponent(r.target_user_id),
    body: pick(r, ["admin_password"]),
    envelope: okEnvelope,
  }),
  admin_init_user_update_password: (r) => ({
    method: 'POST',
    path: 'api/admin/user/' + encodeURIComponent(r.target_user_id) + '/resetPassword',
    body: pick(r, ["admin_password", "new_password"]),
    envelope: okEnvelope,
  }),
  admin_edit_school: (r) => ({
    method: 'PATCH',
    path: 'api/admin/school/' + encodeURIComponent(r.target_school_id),
    body: pick(r, ["admin_password", "name", "name_eng", "key", "locale", "school_type", "school_level"], ["school_type", "school_level"]),
    envelope: okEnvelope,
  }),

  // ------------------------------------------ Extra Locale (預製教材範本)
  // Teacher surface: the selection dialog reads ONE overview (eligibility +
  // quota + the school's permanent selections + the enabled catalog) and
  // writes picks through /select. There is deliberately NO delete cmd — the
  // ledger is insert-only (範本一經選定，不設更換).
  get_extra_locale_overview: (r) => ({
    method: 'GET',
    path: 'api/extraLocale/overview',
    query: q({ school_id: r.school_id }),
  }),
  select_extra_locales: (r) => ({
    method: 'POST',
    path: 'api/extraLocale/select',
    body: b({ extra_locale_ids: r.extra_locale_ids, school_id: r.school_id }),
    // Designed refusals arrive as 403/409 with a typed detail; surface them as
    // the legacy 200-shaped failure so callbackFail gets err.body.remarks AND
    // the typed context (quota/used) survives for the dialog's message.
    onError: (payload, status) => {
      const detail = payload && typeof payload === 'object' ? payload.detail : null;
      if ((status === 403 || status === 409) && detail) {
        return {
          result: -2,
          count: 0,
          error_detail: (typeof detail === 'object') ? detail : { error: String(detail) },
        };
      }
      return null;
    },
  }),
  // Admin surface (platform admins, role >= 3). A catalog row IS an epack
  // (2026-08-27): epack_id is the identity, names resolve from epack.name —
  // there are no code/name fields on the write models any more.
  admin_extra_locale_list: () => ({ method: 'GET', path: 'api/admin/extraLocale/list' }),
  // `manual_file_id` is the 教師參考手冊 PDF: a `file` id the dialog already
  // uploaded and confirmed through the ordinary blob flow. It is in the NUMERIC
  // list, which matters for the DETACH — pick() keeps a numeric 0 (only "" and
  // NaN-ish strings are dropped), and 0 is how "remove the manual" travels.
  admin_extra_locale_create: (r) => ({
    method: 'POST',
    path: 'api/admin/extraLocale',
    body: pick(r, ["category", "remarks", "epack_id", "category_index", "display_index", "is_enabled", "manual_file_id"],
      ["epack_id", "category_index", "display_index", "is_enabled", "manual_file_id"]),
    envelope: idEnvelope,
  }),
  admin_extra_locale_update: (r) => ({
    method: 'PATCH',
    path: 'api/admin/extraLocale/' + encodeURIComponent(r.id),
    body: pick(r, ["category", "remarks", "epack_id", "category_index", "display_index", "is_enabled", "manual_file_id"],
      ["epack_id", "category_index", "display_index", "is_enabled", "manual_file_id"]),
    envelope: okEnvelope,
  }),
  admin_extra_locale_delete: (r) => ({
    method: 'DELETE',
    path: 'api/admin/extraLocale/' + encodeURIComponent(r.id),
    envelope: okEnvelope,
  }),
  // `date_from` / `date_to` are INCLUSIVE bounds on the selection time — the CSV
  // export's start and end dates — as naive local wall-clock with no offset.
  // Bare 'YYYY-MM-DD' is the normal form: the server reads date_from as that
  // day's 00:00:00 and date_to as its 23:59:59, so the same date at both ends
  // means that whole day. Bounding server-side is what stops `limit` from
  // capping the newest records before the range is applied. A server predating
  // the parameters ignores them; the caller re-cuts the window locally, so the
  // sheet is correct either way and only its reach differs.
  admin_extra_locale_selections: (r) => ({
    method: 'GET',
    path: 'api/admin/extraLocale/selections',
    query: q({ keyword: r.keyword, school_id: r.school_id, limit: r.limit,
               date_from: r.date_from, date_to: r.date_to }),
  }),
  // Per-school used/quota + selected names, for the admin CSV export.
  admin_extra_locale_school_summary: () => ({
    method: 'GET',
    path: 'api/admin/extraLocale/schoolSummary',
  }),
  // Scheduled Selection History mail — who receives it and which schedules run.
  // The MAIL itself is sent by an ACA job (ev-extra-locale-report, 09:00 HK:
  // yesterday every day, the previous Mon..Sun additionally on Mondays); these
  // three cmds only manage its settings and offer a manual send.
  admin_extra_locale_report_settings: () => ({
    method: 'GET',
    path: 'api/admin/extraLocale/reportSettings',
  }),
  // PATCH semantics behind a PUT: only the fields present are written, so each
  // switch can save on its own without re-posting the recipient list. `pick`
  // drops undefined, which is what makes that work — a switch-only save must
  // not send `recipients: ""` and silently clear the list.
  //
  // `recipients` is FREE TEXT (the settings textarea's contents), not a list:
  // the server parses, validates and stores it normalised, and 400s on a token
  // that is not an address rather than dropping it. Booleans go through `pick`'s
  // true->1 coercion, which FastAPI reads back as a bool.
  admin_extra_locale_report_settings_save: (r) => ({
    method: 'PUT',
    path: 'api/admin/extraLocale/reportSettings',
    body: pick(r, ["recipients", "daily_enabled", "weekly_enabled", "send_when_empty"]),
  }),
  // Send one report NOW, to the configured list, over the REAL window `kind`
  // names. A 502 carries the SMTP reason verbatim — this is the one place a
  // human is watching, so it is the one place the error is worth showing.
  // Today / past 7 days / past 30 days. `period` is day | days_7 | days_30;
  // anything else is a 422. The payload is one object (totals, the previous
  // window of the same length, and a daily series) — the default envelope
  // already lands it on res.body.result.
  admin_platform_dashboard: (r) => ({
    method: 'GET',
    path: 'api/admin/extraLocale/dashboard',
    query: q({ period: r.period || 'day' }),
  }),
  admin_extra_locale_report_send: (r) => ({
    method: 'POST',
    path: 'api/admin/extraLocale/reportSend',
    body: b({ kind: r.kind || 'daily' }),
  }),

  // -------------------------------------------- admin / EdCity SCRN mapping
  admin_edcity_scrn_list: (r) => ({
    method: 'GET',
    path: 'api/admin/edcityScrn',
    query: q({ keyword: r.keyword, mapped: r.mapped, limit: r.limit, order_by: r.order_by, order: r.order }),
  }),
  admin_edcity_scrn_create: (r) => ({
    method: 'POST',
    path: 'api/admin/edcityScrn',
    body: pick(r, ["school_code", "school_id", "note"], ["school_id"]),
    envelope: okEnvelope,
  }),
  // school_id: 0 clears the connection ("not yet mapped"); pick()'s numeric
  // filter keeps 0 but drops "" so an untouched field is simply not sent.
  admin_edcity_scrn_update: (r) => ({
    method: 'PATCH',
    path: 'api/admin/edcityScrn/' + encodeURIComponent(r.school_code),
    body: pick(r, ["school_id", "note"], ["school_id"]),
    envelope: okEnvelope,
  }),
  admin_edcity_scrn_delete: (r) => ({
    method: 'DELETE',
    path: 'api/admin/edcityScrn/' + encodeURIComponent(r.school_code),
    envelope: okEnvelope,
  }),
  // CSV import, two-phase like user_admin_batch_create_users: validate_only
  // (default) dry-runs every row and the AI name check; the commit re-checks
  // and writes. `rows` is what tools/edcityImportCsv.js parsed, verbatim.
  // The result object ({rows, valid_count, ...}) is the legacy `result`.
  admin_edcity_scrn_import: (r) => ({
    method: 'POST',
    path: 'api/admin/edcityScrn/import',
    body: b({
      rows: r.rows,
      validate_only: r.validate_only,
      run_ai: r.run_ai,
      date_start: r.date_start,
      date_expire: r.date_expire,
    }),
  }),

  // ------------------------------- admin / Subscription Binding (plan -> template)
  admin_subscription_binding_get: () => ({
    method: 'GET',
    path: 'api/admin/subscriptionBinding',
  }),
  // PUT is platform-admin (role >= 3) on the server; GET is any admin.
  admin_subscription_binding_put: (r) => ({
    method: 'PUT',
    path: 'api/admin/subscriptionBinding',
    body: b({ bindings: r.bindings, date_start: r.date_start, date_expire: r.date_expire }),
  }),

  // ----------------------------------------------------------- subscription
  subscription_get: (r) => ({
    method: 'GET',
    path: 'api/subscription/list',
    query: q({
      id: r.id,
      keyword: r.keyword,
      sort_by: r.sort_by,
      sort_dir: r.sort_dir,
      limit: r.limit,
      offset: r.offset,
      is_template: r.is_template,
    }),
  }),
  // subscriptionCreateOK treats `result <= 0` as failure and uses result as the
  // new subscription id.
  subscription_create: (r) => ({
    method: 'POST',
    path: 'api/subscription',
    body: pick(r, ["name", "name_en", "remarks", "is_template"]),
    envelope: idEnvelope,
  }),
  subscription_update: (r) => ({
    method: 'PATCH',
    path: 'api/subscription/' + encodeURIComponent(r.id),
    body: pick(r, ["name", "name_en", "remarks", "is_template"]),
    envelope: (data) => ({ result: (data && data.ok) ? 1 : 0, count: 1 }),
  }),
  // The route answers {ok:true,id} on success and a 409 {error, assigned_schools}
  // when schools are still assigned. subscriptionDeleteOK gates on
  // `result.success === 1` and renders the blocking list off `result.assigned_schools`,
  // so BOTH shapes are translated back to the legacy 200 envelope here —
  // otherwise a delete that succeeded reported "Failed to delete subscription"
  // and the blocked case degraded to a bare "HTTP 409".
  subscription_delete: (r) => ({
    method: 'DELETE',
    path: 'api/subscription/' + encodeURIComponent(r.id),
    body: pick(r, ["admin_password"]),
    envelope: (data) => ({ result: { success: 1, id: (data || {}).id }, count: 1 }),
    onError: (payload, status) => {
      if (status !== 409 || !payload || typeof payload !== 'object') {
        return null;
      }
      return {
        result: {
          success: 0,
          error: payload.error || "subscription has assigned schools",
          assigned_schools: Array.isArray(payload.assigned_schools) ? payload.assigned_schools : [],
        },
        count: 0,
      };
    },
  }),
  subscription_epack_list: (r) => ({ method: 'GET', path: 'api/subscription/' + encodeURIComponent(r.subscription_id) + '/epacks' }),
  subscription_epack_add: (r) => {
    // Two shapes reach this cmd: a single {epack_id} from the locale picker and
    // an {epack_array:[{epack_id}]} from "copy from another subscription".
    let ids = [];
    if (Array.isArray(r.epack_ids)) {
      ids = r.epack_ids;
    } else if (Array.isArray(r.epack_array)) {
      ids = r.epack_array.map((x) => ((x && typeof x === 'object') ? x.epack_id : x));
    } else if (r.epack_id !== undefined && r.epack_id !== null) {
      ids = [r.epack_id];
    }
    return {
      method: 'POST',
      path: 'api/subscription/' + encodeURIComponent(r.subscription_id) + '/epack',
      body: b({ epack_ids: ids }),
    };
  },
  subscription_epack_delete: (r) => ({ method: 'DELETE', path: 'api/subscription/epack/' + encodeURIComponent(r.id) }),
  // The two cmds that SEED a brand-new subscription's privilege rows (reached
  // through AdminTaskDialog.apiRequestPromise, not a literal API.request call).
  // Both branches of subscriptionCreateOK need them — the "copy from" branch AND
  // the default "seed from the catalogue" one — so without a route every newly
  // created subscription was left permanently privilege-less while the dialog
  // reported the creation as failed.
  privilege_get_all: () => ({ method: 'GET', path: 'api/privilege/list' }),
  subscription_privilege_add: (r) => {
    // Two shapes: the catalogue seed sends {privilege_id, value, value_2} rows,
    // and copyPrivilegesFromSubscription forwards subscription_privilege_list
    // rows VERBATIM (id, subscription_id, name, ... as well). The model is
    // extra="forbid", so reduce both to the three columns that matter.
    let rows = [];
    if (Array.isArray(r.privilege_array)) {
      rows = r.privilege_array;
    } else if (r.privilege_id !== undefined && r.privilege_id !== null) {
      rows = [{ privilege_id: r.privilege_id, value: r.value, value_2: r.value_2 }];
    }
    const privileges = rows
      .map((row) => pick(row, ["privilege_id", "value", "value_2"], ["privilege_id", "value", "value_2"]))
      .filter((row) => Number(row.privilege_id) > 0);
    if (privileges.length === 0) {
      // The model requires at least one item; "nothing to seed" is a success,
      // not a 422 that would fail the whole create flow.
      return { special: 'noop' };
    }
    return {
      method: 'POST',
      path: 'api/subscription/' + encodeURIComponent(r.subscription_id) + '/privileges',
      body: b({ privileges: privileges }),
    };
  },
  subscription_privilege_list: (r) => ({ method: 'GET', path: 'api/subscription/' + encodeURIComponent(r.subscription_id) + '/privileges' }),
  subscription_privilege_update: (r) => ({
    method: 'PATCH',
    path: 'api/subscription/privilege/' + encodeURIComponent(r.id),
    body: pick(r, ["value", "value_2"]),
  }),
  get_subscription_list_by_school_id: (r) => ({ method: 'GET', path: 'api/subscription/bySchool', query: q({ school_id: r.school_id }) }),
  get_subscriptions_by_school_id: (r) => ({
    method: 'GET',
    path: 'api/subscription/bySchool',
    query: q({ school_id: r.school_id, include_inactive: r.include_inactive }),
  }),
  get_schools_by_subscription: (r) => ({
    method: 'GET',
    path: 'api/subscription/schools',
    query: q({ subscription_id: r.subscription_id, keyword: r.keyword, school_type: r.school_type, admin_view: r.admin_view, exclude_expired: r.exclude_expired }),
  }),
  school_subscription_add: (r) => ({
    method: 'POST',
    path: 'api/school/' + encodeURIComponent(r.school_id) + '/subscription',
    body: pick(r, ["subscription_id", "date_start", "date_expire", "remark"]),
  }),
  // school_id is now part of the path, so the two call sites in AdminTaskDialog
  // were updated to include it in the payload.
  school_subscription_update: (r) => ({
    method: 'PATCH',
    path: 'api/school/' + encodeURIComponent(r.school_id) + '/subscription/' + encodeURIComponent(r.school_subscription_id),
    body: b({ date_start: r.date_start, date_expire: r.date_expire, remark: r.remark }),
  }),
  school_subscription_delete: (r) => ({
    method: 'DELETE',
    path: 'api/school/' + encodeURIComponent(r.school_id) + '/subscription/' + encodeURIComponent(r.school_subscription_id),
  }),

  // ------------------------------------------- activity log (school admins)
  // Reads are require_admin and PINNED to the token's school on the server: a
  // role-2 admin's `school_id` is ignored, only a platform admin's is honoured.
  // Sent anyway so the CLST case works; nothing here decides scope.
  get_activity_users: (r) => ({
    method: 'GET',
    path: 'api/activity/users',
    query: q({ school_id: r.school_id }),
  }),
  get_activity_list: (r) => ({
    method: 'GET',
    path: 'api/activity/list',
    query: q({
      school_id: r.school_id,
      user_id: r.user_id,
      entity_type: r.entity_type,
      action: r.action,
      entity_id: r.entity_id,
      date_from: r.date_from,
      date_to: r.date_to,
      limit: r.limit,
      offset: r.offset,
    }),
    // {data, count, total, limit, offset} -> the callers read result + total.
    // `payload` is the third envelope argument API.buildBody passes for
    // exactly this reason; `count` is this page's length, `total` the whole.
    envelope: (data, count, payload) => ({
      result: Array.isArray(data) ? data : [],
      count: Array.isArray(data) ? data.length : 0,
      total: payload && typeof payload.total === 'number' ? payload.total : 0,
    }),
  }),
  // Re-author a locale (school admin, own school; the new owner must be a
  // staff account of the LOCALE's school). 400 = not such an account.
  change_epack_owner: (r) => ({
    method: 'PATCH',
    path: 'api/epack/' + encodeURIComponent(r.epack_id) + '/owner',
    body: b({ user_id: r.user_id }),
    envelope: okEnvelope,
    onError: (payload, status) => {
      if (status === 400) {
        return { result: -1, remarks: 'invalid_owner' };
      }
      return null;
    },
  }),

  // ------------------------------------------------- blob upload (FilePicker)
  request_upload: (r) => ({
    method: 'POST',
    path: 'api/upload/request',
    // user_id is dropped — the uploader is the token.
    body: b({ epack_id: r.epack_id, files: r.files }),
  }),
  confirm_upload: (r) => ({ method: 'POST', path: 'api/upload/confirm', body: b({ file_ids: r.file_ids }) }),
};

/**
 * Resolve a cmd to a route descriptor, or null when the cmd is unknown (which
 * API.jsx turns into a loud console.error plus a negative-result failure).
 */
export function resolveRoute(cmd, reqObj) {
  const r = reqObj || {};

  if (Object.prototype.hasOwnProperty.call(GENERIC, cmd)) {
    const byTable = GENERIC[cmd];
    const table = r.table;
    if (!table || !Object.prototype.hasOwnProperty.call(byTable, table)) {
      return null;
    }
    return byTable[table](r);
  }

  if (Object.prototype.hasOwnProperty.call(ROUTES, cmd)) {
    return ROUTES[cmd](r);
  }

  return null;
}

export default resolveRoute;
